import React, { useEffect, useMemo, useState } from "react";
import { useResendVerificationEmail } from "../../hooks/auth";
import { useLocation, useNavigate } from "react-router-dom";
import {
  alpha,
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Fade,
  Grid,
  Stack,
  Typography,
  Alert,
  useTheme,
  Link as MuiLink,
} from "@mui/material";
import {
  Send as SendIcon,
  MarkEmailRead,
  HourglassEmpty,
  CheckCircleOutline,
  MailOutline,
  InfoOutlined,
} from "@mui/icons-material";
import SurfaceCard from "../../components/common/ui/SurfaceCard";
import { ApiError } from "../../error/errorTypes";
import { LOGIN_ROUTE_PATH } from "../../utils/constants/AppConstants";
import BrandIcon from "../../components/common/BrandIcon";

type LocationState = { email?: string } | null;

const VerifyEmailPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as LocationState) || null;
  const email = useMemo(() => state?.email || "", [state]);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cooldownEndsAt, setCooldownEndsAt] = useState<number | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number>(0);

  const { mutate: resendVerificationEmail, isPending: isSending } = useResendVerificationEmail();

  const handleResend = () => {
    setMessage(null);
    setError(null);
    
    const publicBase = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
    const path = `${publicBase}${"/email/verify"}`;
    const verificationCallbackUrl = new URL(path, window.location.origin).toString();
    
    resendVerificationEmail(
      { email, verificationCallbackUrl },
      {
        onSuccess: () => {
          setMessage("Verification email sent. Please check your inbox and spam folder.");
          const endsAt = Date.now() + 60_000; // 60s cooldown
          setCooldownEndsAt(endsAt);
          setSecondsLeft(60);
        },
        onError: (err) => {
          if (err instanceof ApiError) {
            setError(err.userMessage || err.message);
          } else {
            setError("Could not send verification email. Please try again.");
          }
        }
      }
    );
  };

  useEffect(() => {
    if (!cooldownEndsAt) return;
    const interval = setInterval(() => {
      const left = Math.max(0, Math.ceil((cooldownEndsAt - Date.now()) / 1000));
      setSecondsLeft(left);
      if (left <= 0) {
        setCooldownEndsAt(null);
        clearInterval(interval);
      }
    }, 250);
    return () => clearInterval(interval);
  }, [cooldownEndsAt]);

  const isCooldown = cooldownEndsAt !== null;

  const webmailLink = useMemo(() => {
    const domain = (email.split("@")[1] || "").toLowerCase();
    if (domain.includes("gmail.")) return "https://mail.google.com/";
    if (domain.includes("outlook.") || domain.includes("live.") || domain.includes("hotmail.")) return "https://outlook.live.com/mail/";
    if (domain.includes("yahoo.")) return "https://mail.yahoo.com/";
    if (domain.includes("proton.")) return "https://mail.proton.me/";
    return "";
  }, [email]);


  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `radial-gradient(circle at 18% 16%, ${alpha(theme.palette.primary.main, 0.16)} 0%, transparent 38%), ${theme.palette.background.default}`,
        display: "flex",
        alignItems: "center",
        py: { xs: 2, md: 4 },
      }}
    >
      <Container maxWidth="lg">
        <Fade in={true} timeout={700}>
          <SurfaceCard
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
              background: alpha(theme.palette.background.paper, 0.9),
              backdropFilter: "blur(8px)",
              boxShadow: "0 14px 44px rgba(2, 6, 23, 0.55)",
            }}
          >
            <Grid container>
              <Grid
                item
                xs={12}
                md={5}
                sx={{
                  p: { xs: 2.6, sm: 3.2, md: 3.4 },
                  borderRight: { md: `1px solid ${alpha(theme.palette.text.primary, 0.08)}` },
                  borderBottom: { xs: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`, md: "none" },
                  background: `linear-gradient(165deg, ${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.background.paper, 0.35)})`,
                }}
              >
                <Box sx={{ mb: 2.2 }}>
                  <BrandIcon size={48} />
                </Box>

                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: "-0.02em",
                    fontFamily: '"Space Grotesk", "Manrope", sans-serif',
                  }}
                >
                  Verify your email
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.8, maxWidth: 360 }}>
                  Confirm your email to activate your account and secure sign-in access.
                </Typography>

                <Stack spacing={1.05} sx={{ mt: 2.2 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar sx={{ width: 24, height: 24, bgcolor: alpha(theme.palette.primary.main, 0.2), color: theme.palette.primary.light }}>
                      <CheckCircleOutline fontSize="small" />
                    </Avatar>
                    <Typography variant="body2" color="text.secondary">Open the message from PocketBalance</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar sx={{ width: 24, height: 24, bgcolor: alpha(theme.palette.primary.main, 0.2), color: theme.palette.primary.light }}>
                      <MailOutline fontSize="small" />
                    </Avatar>
                    <Typography variant="body2" color="text.secondary">Click the verification link once</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar sx={{ width: 24, height: 24, bgcolor: alpha(theme.palette.primary.main, 0.2), color: theme.palette.primary.light }}>
                      <InfoOutlined fontSize="small" />
                    </Avatar>
                    <Typography variant="body2" color="text.secondary">Return here if you need to resend</Typography>
                  </Stack>
                </Stack>

                <Chip
                  icon={<MarkEmailRead />}
                  label="Email verification required"
                  sx={{
                    mt: 2.2,
                    borderRadius: "10px",
                    color: theme.palette.primary.light,
                    backgroundColor: alpha(theme.palette.primary.main, 0.16),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={7} sx={{ p: { xs: 2.6, sm: 3.2, md: 3.8 } }}>
                <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 2.1 }}>
                  <Avatar sx={{ width: 34, height: 34, bgcolor: alpha(theme.palette.primary.main, 0.2), color: theme.palette.primary.light }}>
                    <SendIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                      Check your inbox
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      We sent a verification email with your activation link.
                    </Typography>
                  </Box>
                </Stack>

                {email ? (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Verification email sent to
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.primary.light,
                        wordBreak: "break-all",
                        mt: 0.7,
                      }}
                    >
                      {email}
                    </Typography>
                  </Box>
                ) : (
                  <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                    We could not determine your email from the previous step. Return to sign in and try again.
                  </Alert>
                )}

                {message && (
                  <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} role="status" aria-live="polite">
                    {message}
                  </Alert>
                )}

                {error && (
                  <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} role="alert">
                    {error}
                  </Alert>
                )}

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={!email || isSending || isCooldown}
                  onClick={handleResend}
                  startIcon={isCooldown ? <HourglassEmpty /> : <SendIcon />}
                  sx={{
                    py: 1.35,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    borderRadius: theme.radii?.medium ?? 10,
                    "&:hover": {
                      transform: "translateY(-1px)",
                      boxShadow: `0 10px 28px ${alpha(theme.palette.primary.main, 0.28)}`,
                    },
                  }}
                >
                  {isSending ? "Sending..." : isCooldown ? `Resend available in ${secondsLeft}s` : "Resend verification email"}
                </Button>

                <Divider sx={{ my: 2 }} />

                <Stack direction="column" spacing={1.15} sx={{ color: "text.secondary" }}>
                  <Typography variant="body2">
                    Did not receive the email? Check spam/promotions, then resend.
                  </Typography>
                  {webmailLink && (
                    <Typography variant="body2">
                      Quick open: <MuiLink href={webmailLink} target="_blank" rel="noopener">open your inbox</MuiLink>
                    </Typography>
                  )}
                </Stack>

                <Box sx={{ textAlign: "center", mt: 2.2 }}>
                  <Button variant="text" onClick={() => navigate(LOGIN_ROUTE_PATH)}>
                    Back to sign in
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </SurfaceCard>
        </Fade>
      </Container>
    </Box>
  );
};

export default VerifyEmailPage;


